<template>
  <div id="payment-success" class="success-page">
    <div class="vx-row">
      <div class="vx-col w-full">
        <vx-card class="text-center success-card">
          <div class="py-12">
            <!-- Success Icon -->
            <div class="success-icon mb-6">
              <i class="fas fa-check-circle text-6xl text-success"></i>
            </div>
            
            <!-- Success Message -->
            <h1 class="text-3xl font-bold text-success mb-4 success-title">
              Payment Successful!
            </h1>
            
            <p class="text-lg text-gray-600 mb-6 success-description">
              Thank you for your purchase! Your TCVelik license is being activated.
            </p>
            
            <!-- License Info -->
            <div v-if="hostname" class="license-info mb-6">
              <div class="bg-gray-50 p-4 rounded-lg license-details">
                <h3 class="font-semibold mb-2 license-title">License Details</h3>
                <p class="text-sm text-gray-600">
                  <strong>Hostname:</strong> {{ hostname }}
                </p>
                <p class="text-sm text-gray-600">
                  <strong>Status:</strong> 
                  <span :class="checkingStatus ? 'text-warning' : 'text-success'">
                    {{ checkingStatus ? 'Checking...' : 'Activating...' }}
                  </span>
                </p>
              </div>
            </div>
            
            <!-- Action Buttons -->
            <div class="action-buttons">
              <vs-button 
                color="primary" 
                size="large"
                @click="goToDashboard"
                class="mr-4 action-btn">
                <i class="fas fa-home mr-2"></i>
                Go to Dashboard
              </vs-button>
              
              <vs-button 
                color="secondary" 
                size="large"
                @click="checkLicenseStatus"
                :loading="checkingStatus"
                class="action-btn">
                <i class="fas fa-sync mr-2"></i>
                Check License Status
              </vs-button>
            </div>
            
            <!-- Additional Info -->
            <div class="additional-info mt-8">
              <p class="text-sm text-gray-500">
                Your license will be automatically activated within a few minutes.
                If you have any questions, please contact our support team.
              </p>
            </div>
          </div>
        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import TCVelikClient from '@/TCVelikClient/TCVelikClient.js';

export default {
  name: 'PaymentSuccess',
  data() {
    return {
      hostname: '',
      checkingStatus: false
    };
  },
  mounted() {
    // Get hostname from URL or window location
    this.hostname = window.location.hostname;
    
    // Auto-check license status after 3 seconds
    setTimeout(() => {
      this.checkLicenseStatus();
    }, 3000);
    
    // Continue checking every 10 seconds for up to 2 minutes
    let attempts = 0;
    const maxAttempts = 12; // 2 minutes / 10 seconds
    
    const checkInterval = setInterval(() => {
      attempts++;
      if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        return;
      }
      
      this.checkLicenseStatus().then(success => {
        if (success) {
          clearInterval(checkInterval);
        }
      });
    }, 10000);
  },
  methods: {
    goToDashboard() {
      this.$router.push('/pages/home');
    },
    
    async checkLicenseStatus() {
      this.checkingStatus = true;
      try {
        const response = await TCVelikClient.GetRelative('/api/licensing/status');
        const status = response.data;
        
        if (status.IsValid) {
          this.$vs.notify({
            title: 'License Activated!',
            text: 'Your license has been successfully activated.',
            color: 'success'
          });
          
          // Redirect to dashboard after successful activation
          setTimeout(() => {
            this.goToDashboard();
          }, 2000);
          
          return true; // Indicate success to stop checking
        } else {
          this.$vs.notify({
            title: 'License Pending',
            text: 'Your license is still being processed. Please wait a moment.',
            color: 'warning'
          });
          
          return false; // Continue checking
        }
      } catch (error) {
        console.error('Error checking license status:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to check license status. Please try again.',
          color: 'danger'
        });
        
        return false; // Continue checking
      } finally {
        this.checkingStatus = false;
      }
    }
  }
};
</script>

<style scoped>
/* Import Outfit font */
@import url('https://fonts.googleapis.com/css?family=Outfit:300,400,500,600,700');

.success-page {
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  align-items: center;
  font-family: 'Outfit', sans-serif;
}

.success-card {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  border: none;
}

.success-icon {
  animation: bounceIn 0.6s ease-out;
}

@keyframes bounceIn {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.success-title {
  font-family: 'Outfit', sans-serif;
  font-weight: 700;
  color: #10b981;
}

.success-description {
  font-family: 'Outfit', sans-serif;
  font-weight: 400;
  line-height: 1.6;
}

.license-info {
  max-width: 400px;
  margin: 0 auto;
}

.license-details {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.license-title {
  font-family: 'Outfit', sans-serif;
  font-weight: 600;
  color: #374151;
}

.action-buttons {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.action-btn {
  font-family: 'Outfit', sans-serif;
  font-weight: 600;
  min-width: 180px;
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.additional-info {
  max-width: 500px;
  margin: 0 auto;
  font-family: 'Outfit', sans-serif;
  font-weight: 400;
}

.additional-info p {
  line-height: 1.6;
}

/* Loading animation for status */
.text-warning {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@media (max-width: 768px) {
  .success-page {
    padding: 1rem;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .action-btn {
    width: 100%;
    max-width: 300px;
  }
  
  .success-title {
    font-size: 2rem;
  }
  
  .success-description {
    font-size: 1rem;
  }
}
</style>
